import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useMemo, useState } from 'react';
import useSWR from 'swr';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { GlobalContext } from '../../contexts/GlobalContext';
import { StyledCardSection } from '../base/Card';
import { IconName } from '../base/Icon';
import Tabs from '../base/Tabs';
import Dropdown from '../Dropdown';
import ErrorBoundary from '../helpers/ErrorBoundary';
import BulkRequestOperationsHistory from './BulkRequestOperationsHistory';
import SubmitBulkRequestRetryReplay from './SubmitBulkRequestRetryReplay';
import { BulkOperationFilters } from './types';
import { ensureDateFilters } from './utils';
interface Props {
  filters: BulkOperationFilters;
  onSubmit?: () => void;
}
const DropdownContent: React.FC<Props & {
  onSubmit?: () => void;
}> = t0 => {
  const $ = _c(51);
  const {
    filters: filters_prop
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const [active_tab, setActiveTab] = useState("conditions");
  let t1;
  if ($[0] !== filters_prop) {
    t1 = ensureDateFilters(filters_prop);
    $[0] = filters_prop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const filters = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = APIMethodKeys.bulk.requests.count({
      in_progress: true
    });
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== HookdeckAPI.bulk.requests) {
    t3 = () => HookdeckAPI.bulk.requests.count({
      in_progress: true
    });
    $[3] = HookdeckAPI.bulk.requests;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: retry_ongoing,
    mutate: mutate_retry_ongoing
  } = useSWR(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = APIMethodKeys.bulk.requests.count({
      in_progress: false
    });
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== HookdeckAPI.bulk.requests) {
    t5 = () => HookdeckAPI.bulk.requests.count({
      in_progress: false
    });
    $[6] = HookdeckAPI.bulk.requests;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: retry_history_count,
    mutate: mutate_retry_history
  } = useSWR(t4, t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = APIMethodKeys.bulk.requestsReplay.count({
      in_progress: true
    });
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== HookdeckAPI.bulk.requestsReplay) {
    t7 = () => HookdeckAPI.bulk.requestsReplay.count({
      in_progress: true
    });
    $[9] = HookdeckAPI.bulk.requestsReplay;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    data: replay_ongoing,
    mutate: mutate_replay_ongoing
  } = useSWR(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = APIMethodKeys.bulk.requestsReplay.count({
      in_progress: false
    });
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== HookdeckAPI.bulk.requestsReplay) {
    t9 = () => HookdeckAPI.bulk.requestsReplay.count({
      in_progress: false
    });
    $[12] = HookdeckAPI.bulk.requestsReplay;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    data: replay_history_count,
    mutate: mutate_replay_history
  } = useSWR(t8, t9);
  let t10;
  if ($[14] !== mutate_replay_history || $[15] !== mutate_replay_ongoing || $[16] !== mutate_retry_history || $[17] !== mutate_retry_ongoing) {
    t10 = () => {
      mutate_retry_ongoing();
      mutate_retry_history();
      mutate_replay_ongoing();
      mutate_replay_history();
    };
    $[14] = mutate_replay_history;
    $[15] = mutate_replay_ongoing;
    $[16] = mutate_retry_history;
    $[17] = mutate_retry_ongoing;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const revalidate_counts = t10;
  const ongoing_count = (retry_ongoing?.count ?? 0) + (replay_ongoing?.count ?? 0);
  const history_count = (retry_history_count?.count ?? 0) + (replay_history_count?.count ?? 0);
  let t11;
  if ($[19] !== active_tab || $[20] !== revalidate_counts) {
    t11 = () => {
      revalidate_counts();
      if (active_tab === "conditions") {
        setActiveTab("ongoing");
      }
    };
    $[19] = active_tab;
    $[20] = revalidate_counts;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const after_submit = t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      label: "Conditions",
      key: "conditions",
      icon: "checklist" as IconName
    };
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = ongoing_count > 0 ? "primary" as const : "muted" as const;
  let t14;
  if ($[23] !== ongoing_count || $[24] !== t13) {
    t14 = {
      label: "Ongoing",
      key: "ongoing",
      icon: "retry" as IconName,
      badge_label: ongoing_count,
      badge_theme: t13
    };
    $[23] = ongoing_count;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== history_count) {
    t15 = {
      label: "History",
      key: "history",
      icon: "history" as IconName,
      badge_label: history_count,
      badge_theme: "muted" as const
    };
    $[26] = history_count;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = [t12, t14, t15];
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const tabs = t16;
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      x: 4,
      t: 3
    };
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== active_tab || $[33] !== tabs) {
    t18 = <StyledCardSection><Tabs onTabSelected={setActiveTab} active_tab={active_tab} border={false} p={t17} tabs={tabs} /></StyledCardSection>;
    $[32] = active_tab;
    $[33] = tabs;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== active_tab || $[36] !== after_submit || $[37] !== filters || $[38] !== revalidate_counts) {
    t19 = active_tab === "conditions" && <SubmitBulkRequestRetryReplay query={filters} revalidateCounts={revalidate_counts} onSubmit={after_submit} />;
    $[35] = active_tab;
    $[36] = after_submit;
    $[37] = filters;
    $[38] = revalidate_counts;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== active_tab || $[41] !== revalidate_counts) {
    t20 = active_tab === "ongoing" && <BulkRequestOperationsHistory filters={{
      in_progress: true
    }} revalidateCounts={revalidate_counts} />;
    $[40] = active_tab;
    $[41] = revalidate_counts;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== active_tab || $[44] !== revalidate_counts) {
    t21 = active_tab === "history" && <BulkRequestOperationsHistory filters={{}} revalidateCounts={revalidate_counts} />;
    $[43] = active_tab;
    $[44] = revalidate_counts;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t18 || $[47] !== t19 || $[48] !== t20 || $[49] !== t21) {
    t22 = <>{t18}{t19}{t20}{t21}</>;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  return t22;
};

/**
 * Unified bulk action dropdown for the Requests view. Replaces the previous
 * separate `BulkOperationDropdown(retry, requests)` + `BulkReplayButton` pair.
 *
 * The toggle inside the Conditions tab promotes the action from a Retry
 * (resends existing events) to a Replay (re-ingests through the pipeline,
 * targeting a chosen set of connections). The Ongoing/History tabs show
 * retries and replays merged chronologically, independent of the toggle.
 */
const BulkRequestActionDropdown: React.FC<Props> = t0 => {
  const $ = _c(15);
  const {
    filters,
    onSubmit
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = APIMethodKeys.bulk.requests.count({
      in_progress: true
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== HookdeckAPI.bulk.requests) {
    t2 = () => HookdeckAPI.bulk.requests.count({
      in_progress: true
    });
    $[1] = HookdeckAPI.bulk.requests;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: retry_ongoing
  } = useSWR(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = APIMethodKeys.bulk.requestsReplay.count({
      in_progress: true
    });
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== HookdeckAPI.bulk.requestsReplay) {
    t4 = () => HookdeckAPI.bulk.requestsReplay.count({
      in_progress: true
    });
    $[4] = HookdeckAPI.bulk.requestsReplay;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data: replay_ongoing
  } = useSWR(t3, t4);
  const total_ongoing = (retry_ongoing?.count ?? 0) + (replay_ongoing?.count ?? 0);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      min: 624,
      max: 624
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== total_ongoing) {
    t6 = total_ongoing > 0 ? {
      label: String(total_ongoing)
    } : undefined;
    $[7] = total_ongoing;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== filters || $[10] !== onSubmit) {
    t7 = <ErrorBoundary><DropdownContent filters={filters} onSubmit={onSubmit} /></ErrorBoundary>;
    $[9] = filters;
    $[10] = onSubmit;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <Dropdown icon="retry" outline={true} parent_width={t5} label="Bulk retry" expand_icon="chevron_down" portal={true} badge={t6}>{t7}</Dropdown>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
export default BulkRequestActionDropdown;